import { foldUnit } from "@/lib/units/unit-options";
import type { Material } from "@/types/material";

/**
 * Units a thing is bought in that measure nothing on their own. A copy of the
 * API's `CONTAINER_UNITS` (`common/units/unit-conversion.ts`), because a plain
 * formatter has no `GET /units` result to ask; keep the two in step.
 */
const CONTAINER_UNITS = new Set(
  [
    "case", "box", "bag", "pack", "carton", "crate", "tray", "jar", "bottle", "can", "tin", "tub", "pail",
    "bucket", "drum", "keg", "sack", "bunch", "bundle", "loaf", "roll", "punnet", "clamshell", "pouch",
  ].map(foldUnit),
);

function isContainer(unit: string): boolean {
  const folded = foldUnit(unit);
  // "cases" and "boxes" are the plural of a container, not a different unit.
  return [folded, folded.replace(/s$/, ""), folded.replace(/es$/, "")].some((name) => CONTAINER_UNITS.has(name));
}

/**
 * Pack counts arrive as Decimal(12,4), so a whole 85 comes back as "85.0000".
 * Trailing zeros read as false precision on a count of apples.
 */
export function formatPackQuantity(quantity: number | string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return String(quantity);
  return String(Number(value.toFixed(4)));
}

/**
 * How an ingredient is bought, in one line: a case that holds 85 each reads
 * "1 case (85 each)". Without a pack size there is nothing to add, so this
 * falls back to the purchase unit on its own.
 *
 * The pack pair is read as "1 <purchase unit> = <quantity> <pack unit>". An
 * ingredient entered the other way round, purchase unit "each" with a pack of
 * 80 case, would read "1 each (80 case)". When only the pack unit is a
 * container the pair is flipped to say what it means: "80 each (1 case)".
 */
export function formatPackSize(
  material: Pick<Material, "unitOfMeasure" | "packQuantity" | "packUom">,
): string {
  const unit = material.unitOfMeasure?.trim() ?? "";
  const quantity = material.packQuantity;
  const packUom = material.packUom?.trim();

  if (quantity == null || !packUom) return unit;

  if (unit !== "" && isContainer(packUom) && !isContainer(unit)) {
    return `${formatPackQuantity(quantity)} ${unit} (1 ${packUom})`;
  }

  return `1 ${unit} (${formatPackQuantity(quantity)} ${packUom})`;
}
